import { Link, useNavigate } from "react-router-dom";
import DirectionsButton from "../components/DirectionsButton";
import { useEffect, useMemo, useState } from "react";
import { Button } from "../components/ui/button";
import { MapPin, Storefront, Clock, Lightning, ArrowRight, Flame, ForkKnife, CalendarCheck, Waves } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { useAuth } from "../lib/auth";
import api, { API } from "../lib/api";
import { trackSiteEvent } from "../lib/analytics";
import LandingMiniMap from "../components/LandingMiniMap";
import ProPinShowcase from "../components/ProPinShowcase";
import AppPromoSection from "../components/AppPromoSection";
import EventRequestBanner from "../components/EventRequestBanner";
import PageMeta from "../components/PageMeta";
import Testimonials from "../components/Testimonials";

const HOME_JSON_LD = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "TruckTrackr",
    url: "https://thetrucktrackr.com",
    logo: "https://thetrucktrackr.com/icon-512.png",
    email: "support@thetrucktrackr.com",
    foundingLocation: { "@type": "Place", name: "Lancaster, PA" },
    sameAs: [
      "https://apps.apple.com/us/app/thetrucktrackr/id6783500007",
      "https://play.google.com/store/apps/details?id=com.thetrucktrackr.app",
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "TruckTrackr",
    url: "https://thetrucktrackr.com",
  },
];

const NEARBY_RADIUS_MILES = 10;

// Public-facing truck count never displays below this floor (the platform
// publicly announced 202 trucks; published-profile counts can dip slightly
// as profiles are edited/unpublished). Live count takes over past 202.
const TRUCK_COUNT_FLOOR = 202;
const displayTruckCount = (total) =>
  total != null ? Math.max(total, TRUCK_COUNT_FLOOR) : null;

function haversineMiles(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/** Parse "New Holland, PA ..." → "New Holland". Best-effort, never throws. */
function cityFromAddress(addr) {
  if (!addr || typeof addr !== "string") return null;
  const parts = addr.split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) return parts[parts.length - 2].split(" ").slice(0, 3).join(" ");
  return null;
}

/**
 * Group events by owner (one "truck" per owner+truck_name) and return the
 * grouping for downstream selection.
 */
function groupByTruck(events) {
  const map = new Map();
  for (const e of events) {
    const key = `${e.owner_id}|${e.truck_name}`;
    const list = map.get(key) || [];
    list.push(e);
    map.set(key, list);
  }
  return Array.from(map.values()).map((list) => ({
    owner_id: list[0].owner_id,
    truck_name: list[0].truck_name,
    events: list.sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at)),
  }));
}

function pickFeatured(weekEvents, nowMs) {
  if (!weekEvents || weekEvents.length === 0) return null;
  const groups = groupByTruck(weekEvents);
  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);
  const todayEndMs = todayEnd.getTime();

  const todayGroups = groups.filter((g) =>
    g.events.some((e) => {
      const s = new Date(e.starts_at).getTime();
      return s >= nowMs - 12 * 3600 * 1000 && s <= todayEndMs;
    }),
  );
  const pool = todayGroups.length > 0 ? todayGroups : groups;
  if (pool.length === 0) return null;
  const group = pool[Math.floor(Math.random() * pool.length)];

  // Find the next upcoming (or currently-open) event for CTAs
  const next = group.events.find((e) => new Date(e.ends_at).getTime() >= nowMs) || group.events[0];
  const isOpen =
    new Date(next.starts_at).getTime() <= nowMs && nowMs < new Date(next.ends_at).getTime();
  const isToday = new Date(next.starts_at).toDateString() === new Date().toDateString();

  const firstImg =
    group.events
      .map((e) => (Array.isArray(e.menu_image_paths) && e.menu_image_paths[0]) || e.menu_image_path)
      .find(Boolean) || null;

  return {
    owner_id: group.owner_id,
    truck_name: group.truck_name,
    event_count: group.events.length,
    next_event: next,
    is_open_now: isOpen,
    is_today: isToday,
    city: cityFromAddress(next?.address),
    image_path: firstImg,
  };
}

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [userPos, setUserPos] = useState(null);
  const [stats, setStats] = useState({ active: null, total: null });

  // Ask geolocation once on mount (non-blocking; banner simply won't render
  // if the user denies). Uses `getUserLocation({requireInteraction:false})`
  // which ONLY returns a position if the browser has already granted
  // geolocation in a previous session. We never trigger the OS prompt
  // here — the user has to explicitly tap a "Use my location" CTA for
  // that. Fixes the repeated-prompt bug.
  useEffect(() => {
    let cancelled = false;
    import("../lib/geo").then(({ getUserLocation }) => {
      getUserLocation({ requireInteraction: false }).then((res) => {
        if (cancelled) return;
        if (res.ok) setUserPos({ lat: res.lat, lng: res.lng });
      });
    });
    return () => { cancelled = true; };
  }, []);

  // Single API call for the whole page — powers both the banner and the
  // featured-truck pick with no extra network roundtrips.
  useEffect(() => {
    let mounted = true;
    api
      .get("/trucks/upcoming", { params: { when: "week" } })
      .then(({ data }) => mounted && setEvents(Array.isArray(data) ? data : []))
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  // OBX Founding Pro spots-left count for the announcement strip.
  const [obxSpotsLeft, setObxSpotsLeft] = useState(null);
  useEffect(() => {
    let mounted = true;
    api
      .get("/preloaded-trucks/stats", { params: { city: "outer-banks-nc" } })
      .then(({ data }) => {
        if (mounted && data?.founding_promo_active && data.founding_slots_available > 0) {
          setObxSpotsLeft(data.founding_slots_available);
        }
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Real platform counts — feeds the proof strip AND the directory promo
  // with one request. Never hardcoded.
  useEffect(() => {
    let cancelled = false;
    api.get("/trucks/active-count")
      .then(({ data }) => {
        if (!cancelled && data) {
          setStats({ active: data.active, total: data.total_profiles });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const nowMs = useMemo(() => Date.now(), [events]);

  const openNowNearbyCount = useMemo(() => {
    if (!userPos || events.length === 0) return 0;
    let count = 0;
    for (const e of events) {
      const s = new Date(e.starts_at).getTime();
      const en = new Date(e.ends_at).getTime();
      if (!(s <= nowMs && nowMs < en)) continue;
      if (haversineMiles(userPos.lat, userPos.lng, e.lat, e.lng) <= NEARBY_RADIUS_MILES) count += 1;
    }
    return count;
  }, [events, userPos, nowMs]);

  // Picked once per mount — refreshes on every page load, per spec.
  const featured = useMemo(() => pickFeatured(events, nowMs), [events, nowMs]);

  const onViewTruck = () => {
    if (!featured?.next_event) return;
    // Send the map to this event's pin. MapView reads ?truck=<id> and opens
    // its detail sheet once the list loads.
    navigate(`/map?truck=${encodeURIComponent(featured.next_event.id)}`);
  };

  const featuredDynamicLine = (() => {
    if (!featured) return "";
    if (featured.is_open_now && featured.city) return `Serving now in ${featured.city}`;
    if (featured.is_today && featured.city) return `Open today in ${featured.city}`;
    if (featured.is_today) return "Serving today";
    if (featured.event_count > 1)
      return `${featured.truck_name} has ${featured.event_count} events this week`;
    return `Catch ${featured.truck_name} this week`;
  })();

  return (
    <div className="relative" data-testid="landing-page">
      <PageMeta
        title="TruckTrackr — Find Food Trucks Near You | Live Map & Schedules"
        description="Live map of local food trucks serving today. See exactly where they're parked, when they open, and follow your favorites for push alerts. Free for everyone."
        canonical="https://thetrucktrackr.com/"
        jsonLd={HOME_JSON_LD}
      />
      {/* 0 — OBX launch announcement strip (always visible) */}
      <Link
        to="/food-trucks/outer-banks-nc"
        onClick={() => trackSiteEvent("obx_banner_click")}
        className="block bg-gradient-to-r from-cyan-700 via-sky-700 to-blue-700 text-white hover:from-cyan-600 hover:via-sky-600 hover:to-blue-600 transition-colors"
        data-testid="obx-launch-banner"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-center gap-2 text-center text-xs sm:text-sm font-semibold">
          <Waves weight="bold" className="h-4 w-4 flex-none" aria-hidden="true" />
          <span>Now servicing the OBX area — Corolla to Hatteras.</span>
          <span className="underline underline-offset-2 hidden sm:inline">See Outer Banks food trucks</span>
          {obxSpotsLeft != null && (
            <span
              className="hidden md:inline-flex items-center rounded-full bg-white/20 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide"
              data-testid="obx-banner-spots-left"
            >
              {obxSpotsLeft} free-Pro-for-life spot{obxSpotsLeft === 1 ? "" : "s"} left for trucks
            </span>
          )}
          <ArrowRight className="h-3.5 w-3.5 flex-none" />
        </div>
      </Link>
      {/* 1 — Hero (consumer-first, unchanged) */}
      <section className="relative overflow-hidden">
        {/* Brand-owned gradient backdrop — no more stock photos as the
            "face" of the site. The OG image (1200×630) handles social
            previews; this gradient sets the atmosphere on the page itself. */}
        <div className="absolute inset-0" aria-hidden="true">
          <div className="absolute inset-0 bg-gradient-to-b from-primary/15 via-background/70 to-background" />
          {/* Subtle dotted texture so the hero isn't a flat color slab */}
          <div
            className="absolute inset-0 opacity-40 dark:opacity-25 pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, rgba(234,88,12,0.18) 1px, transparent 0)",
              backgroundSize: "22px 22px",
            }}
          />
          {/* Warm glow behind the headline */}
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-[460px] w-[680px] rounded-full bg-primary/25 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-16 sm:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs uppercase tracking-[0.2em] text-primary font-medium">
                <Lightning weight="fill" className="h-3 w-3" /> Live today
              </span>
              {openNowNearbyCount > 0 && (
                <motion.span
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35 }}
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-card/80 backdrop-blur px-3 py-1 text-xs font-semibold"
                  data-testid="hero-nearby-banner"
                >
                  <Flame weight="fill" className="h-3.5 w-3.5 text-primary" />
                  {openNowNearbyCount} truck{openNowNearbyCount === 1 ? "" : "s"} open near you right now
                </motion.span>
              )}
            </div>
            <h1 className="mt-6 font-display font-bold tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Find food trucks near you.
              <br />
              <span className="text-primary">Updated daily.</span>
            </h1>
            <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-xl">
              See who's serving today and exactly where to find them.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/map" data-testid="hero-cta-map" onClick={() => trackSiteEvent("cta_find_trucks")}>
                <Button
                  size="lg"
                  className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-6 rounded-full"
                >
                  <MapPin weight="duotone" className="h-5 w-5" />
                  Find trucks near me
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to={user?.role === "owner" ? "/dashboard" : user ? "/map" : "/signup?role=owner"} data-testid="hero-cta-owner" onClick={() => trackSiteEvent("cta_owner_signup")}>
                <Button
                  size="lg"
                  variant="outline"
                  className="gap-2 h-12 px-6 rounded-full border-border"
                >
                  <Storefront weight="duotone" className="h-5 w-5" />
                  I own a truck
                </Button>
              </Link>
            </div>

            <Link
              to="/nearby"
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition"
              data-testid="hero-cta-nearby"
            >
              Just show me what's within 2 miles
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* 2 — Compact platform proof strip (real dynamic data only) */}
      <section
        className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-12 sm:pb-16"
        data-testid="proof-strip"
      >
        <div className="rounded-2xl border border-border bg-card/70 backdrop-blur px-4 sm:px-6 py-3.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-sm">
          <span className="font-semibold text-foreground" data-testid="proof-strip-count">
            {stats.total != null
              ? `${displayTruckCount(stats.total)} food trucks and growing`
              : "Real food trucks, added weekly"}
          </span>
          <span className="text-border">·</span>
          <span className="text-muted-foreground">Live schedules</span>
          <span className="text-border">·</span>
          <span className="text-muted-foreground">Menus</span>
          <span className="text-border">·</span>
          <span className="text-muted-foreground">Directions</span>
        </div>
      </section>

      {/* 3 — Explore / Featured food trucks */}
      <DirectoryPromo stats={stats} />

      {/* Featured Truck — hidden entirely when nothing to feature */}
      {featured && (
        <section
          className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 -mt-10 sm:-mt-16 mb-16 sm:mb-24"
          data-testid="featured-truck-section"
        >
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="grid lg:grid-cols-[280px_1fr] gap-0 overflow-hidden rounded-3xl border border-border bg-card shadow-sm"
          >
            {/* Thumbnail */}
            <div className="relative h-48 lg:h-auto bg-gradient-to-br from-primary/25 via-primary/10 to-background overflow-hidden">
              {featured.image_path ? (
                <img
                  src={`${API}/files/${featured.image_path}`}
                  alt={`${featured.truck_name} menu`}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 grid place-items-center">
                  <Storefront weight="duotone" className="h-16 w-16 text-primary/80" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent lg:bg-gradient-to-r" />
            </div>
            {/* Copy + CTAs */}
            <div className="p-6 sm:p-8 flex flex-col justify-center">
              <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-primary font-semibold">
                <Flame weight="fill" className="h-3.5 w-3.5" /> Featured Truck
              </span>
              <h2
                className="mt-2 font-display font-bold tracking-tight text-2xl sm:text-3xl"
                data-testid="featured-truck-name"
              >
                {featured.truck_name}
              </h2>
              <p
                className="mt-1.5 text-sm text-muted-foreground"
                data-testid="featured-truck-line"
              >
                {featuredDynamicLine}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <Button
                  onClick={onViewTruck}
                  className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-11 px-5"
                  data-testid="featured-view-btn"
                >
                  <MapPin weight="duotone" className="h-4 w-4" />
                  View truck
                </Button>
                {featured.next_event && (
                  <DirectionsButton
                    lat={featured.next_event.lat}
                    lng={featured.next_event.lng}
                    address={featured.next_event.address}
                    label={featured.truck_name}
                    variant="ghost"
                    testId="featured-directions-btn"
                  />
                )}
              </div>
            </div>
          </motion.div>
        </section>
      )}

      {/* 4 — Get the TruckTrackr app (real screenshot, App Store CTA) */}
      <AppPromoSection />

      {/* 5 — How TruckTrackr works (concise consumer benefits) */}
      <section className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-20 sm:pb-28" data-testid="how-it-works-section">
        <div className="grid gap-4 md:grid-cols-3">
          {[
            {
              icon: MapPin,
              title: "See who's nearby",
              body: "A clean map of every truck open today, no clutter, no ads.",
              cta: "Open the map",
              to: "/map",
              testid: "feature-cta-map",
            },
            {
              icon: Clock,
              title: "Check schedules & hours",
              body: "Know exactly when they open and close — only today's posts show up.",
              cta: "See the list",
              to: "/map?view=list",
              testid: "feature-cta-list",
            },
            {
              icon: ForkKnife,
              title: "Menus & directions",
              body: "Browse menus and photos, then get one-tap directions to the window.",
              cta: "Browse trucks",
              to: "/profiles",
              testid: "feature-cta-profiles",
            },
          ].map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
            >
              <Link
                to={f.to}
                data-testid={f.testid}
                className="block h-full rounded-2xl border border-border bg-card p-6 sm:p-7 hover:border-primary/40 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary group"
                aria-label={`${f.title} — ${f.cta}`}
              >
                <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary grid place-items-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <f.icon weight="duotone" className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-display font-semibold text-xl tracking-tight">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.body}</p>
                <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:gap-2.5 transition-all">
                  {f.cta}
                  <ArrowRight className="h-4 w-4" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 6 — Meet the founder (real photo, real identity, no invented stats) */}
      <section
        className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-20"
        data-testid="founder-trust-section"
      >
        <div className="rounded-3xl border border-border bg-card overflow-hidden grid md:grid-cols-[280px_1fr]">
          <div className="relative h-56 sm:h-64 md:h-auto">
            <img
              src="/founder-jason-ember.jpg"
              alt="Jason Lewis, founder of TruckTrackr, standing next to his Ember food truck"
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover object-top"
              data-testid="founder-photo"
            />
          </div>
          <div className="p-6 sm:p-10 flex flex-col justify-center">
            <span className="text-xs uppercase tracking-[0.2em] text-primary font-medium">
              Meet the founder
            </span>
            <h2 className="mt-3 font-display font-bold tracking-tight text-2xl sm:text-3xl">
              Built from behind the window.
            </h2>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl leading-relaxed">
              TruckTrackr was created by <span className="text-foreground font-semibold">Jason Lewis</span>,
              owner of <span className="text-foreground font-semibold">Ember Food</span> in Lancaster
              County, Pennsylvania. After experiencing firsthand how difficult it could be for
              customers to reliably know where food trucks were serving, Jason built a better way
              for trucks and the people looking for them to connect. No commissions, no ordering
              fees, ever.
            </p>
            <div className="mt-4 text-sm">
              <div className="font-semibold text-foreground">Jason Lewis</div>
              <div className="text-muted-foreground">Founder, TruckTrackr · Owner, Ember Food</div>
            </div>
            <Link
              to="/about"
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition"
              data-testid="founder-trust-about-link"
            >
              Meet Jason
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* 7 — Audience transition: Own a food truck? */}
      <section className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-20" data-testid="owner-section">
        <div className="max-w-2xl mb-6">
          <span className="text-xs uppercase tracking-[0.2em] text-primary font-medium">For truck owners</span>
          <h2 className="mt-3 font-display font-bold tracking-tight text-3xl sm:text-4xl">
            Own a <span className="text-primary">food truck?</span>
          </h2>
          <p className="mt-3 text-muted-foreground">
            Put your truck where hungry customers are already looking.
          </p>
        </div>
        <div className="grid lg:grid-cols-2 gap-8 items-center rounded-3xl border border-border bg-card p-6 sm:p-10 overflow-hidden">
          <div>
            <h3 className="font-display font-bold tracking-tight text-2xl sm:text-3xl">
              The fastest way to <span className="text-primary">fill your truck.</span>
            </h3>
            <p className="mt-4 text-muted-foreground">
              Post once in the morning. Hungry locals within a few blocks see your pin, your menu,
              and your hours. No ordering system, no fees, no nonsense.
            </p>
            <Link to={user?.role === "owner" ? "/dashboard" : user ? "/map" : "/signup?role=owner"} data-testid="cta-owner-bottom" onClick={() => trackSiteEvent("cta_owner_signup")} className="inline-block">
              <Button
                size="lg"
                className="mt-6 gap-2 bg-primary text-primary-foreground hover:bg-primary/90 h-12 rounded-full"
              >
                <Storefront weight="duotone" className="h-5 w-5" />
                Get started — free
              </Button>
            </Link>
            <Link
              to="/pricing"
              className="mt-3 ml-0 sm:ml-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition"
              data-testid="cta-owner-pricing"
            >
              See plans &amp; pricing
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <LandingMiniMap />
        </div>
      </section>

      {/* 8 — Owner / Pro benefits (condensed) */}
      <section
        className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-20"
        data-testid="landing-calendar-sync-section"
      >
        <div className="rounded-3xl border border-border bg-gradient-to-br from-card via-card to-primary/5 p-6 sm:p-10 overflow-hidden">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-1.5 px-2 h-6 rounded-full bg-primary/15 text-primary text-[10px] uppercase tracking-wider font-bold">
                <CalendarCheck weight="fill" className="h-3 w-3" />
                Pro
              </div>
              <h2 className="mt-3 font-display font-bold tracking-tight text-3xl sm:text-4xl">
                Manage your schedule once. <span className="text-primary">Publish it everywhere.</span>
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Pro syncs your TruckTrackr schedule with the calendar you already use — the public
                map, your profile, and your followers' alerts stay current automatically.
              </p>
              <Link
                to="/pricing"
                data-testid="landing-calendar-cta"
                onClick={() => trackSiteEvent("cta_pro_features")}
                className="inline-block"
              >
                <Button
                  size="lg"
                  className="mt-6 gap-2 bg-primary text-primary-foreground hover:bg-primary/90 h-12 rounded-full"
                >
                  See everything Pro includes
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="grid grid-cols-3 gap-3 sm:gap-4">
                {[
                  { name: "Google\u00A0Calendar", tag: "two-way sync" },
                  { name: "Outlook", tag: "two-way sync" },
                  { name: "Apple\u00A0Calendar", tag: "calendar feed" },
                ].map((c) => (
                  <div
                    key={c.name}
                    className="rounded-2xl border border-border bg-card p-4 text-center shadow-sm"
                  >
                    <CalendarCheck
                      weight="duotone"
                      className="mx-auto h-7 w-7 text-primary"
                    />
                    <div className="mt-2 font-display font-semibold text-sm text-foreground tracking-tight whitespace-nowrap">
                      {c.name}
                    </div>
                    <div className="mt-1 text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                      {c.tag}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-center text-[11px] text-muted-foreground italic">
                One source of truth. Every device. No double entry.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pro Pin Showcase — real Pro trucks' map pins, doubles as
          social proof for Free owners considering upgrading. Renders
          nothing if the pool is empty so we don't leave an awkward gap. */}
      <ProPinShowcase />

      {/* 9 — Real owner testimonials (renders nothing if list is empty) */}
      <Testimonials />

      {/* 10 — Planning an event? Compact Request-a-Truck intro */}
      <EventRequestBanner />

      {/* 11 — Footer */}
      <footer className="border-t border-border py-8 text-center text-sm text-muted-foreground">
        <p>TruckTrackr — find the street food closest to you.</p>
      </footer>
    </div>
  );
}

/**
 * Directory promo block — sits above the featured truck section.
 *
 * Receives the shared platform stats from Landing (one request feeds
 * both the proof strip and this promo) and renders a high-contrast
 * CTA that points at /profiles.
 *
 * SEO bonus: a crawlable `<Link to="/profiles">` makes Google
 * discover the directory from the homepage without depending on the
 * sitemap.
 */
function DirectoryPromo({ stats }) {
  const shown = displayTruckCount(stats.total);
  const totalLabel = shown != null
    ? `${shown} truck${shown === 1 ? "" : "s"} on TruckTrackr`
    : "Trucks on TruckTrackr";
  return (
    <section
      className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mb-16 sm:mb-24"
      data-testid="directory-promo-section"
    >
      <div className="rounded-3xl border border-border bg-gradient-to-br from-primary/10 via-card to-card p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wider text-primary mb-1.5">
            {totalLabel}
          </p>
          <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight leading-tight">
            Explore local food trucks
          </h2>
          <p className="mt-1.5 text-sm sm:text-base text-muted-foreground max-w-xl">
            Browse every truck active on TruckTrackr — menus, photos, schedules,
            and a live "open now" status. Pro &amp; complete profiles surface first.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3 flex-none">
          <Link
            to="/profiles"
            data-testid="directory-promo-cta-all"
            className="inline-flex items-center justify-center gap-1.5 h-11 px-5 rounded-full bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors"
          >
            <Storefront weight="duotone" className="h-4 w-4" />
            Browse all trucks
            <ArrowRight className="h-4 w-4" />
          </Link>
          {stats.active != null && stats.active > 0 && (
            <Link
              to="/profiles?active=1"
              data-testid="directory-promo-cta-active"
              className="inline-flex items-center justify-center gap-1.5 h-11 px-4 rounded-full border border-border bg-card hover:bg-muted font-semibold text-sm transition-colors"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-70" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              Active this week ({stats.active})
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
